/**
 * AuthScaffold — the per-screen wrapper for every auth form body.
 *
 *   - Split auth (≥900): render a centered ScrollView with the canonical
 *     column width + padding. The `BrandPanel` that used to live here
 *     has been hoisted to `app/(auth)/_layout.tsx` so it's persistent
 *     across route changes (no more entrance-animation jitter). Props
 *     like `brandHeadline` / `brandSubtitle` are ignored on desktop now
 *     — kept for back-compat so no screen has to change its call site.
 *     The split branch is NOT desktop-only: a tablet in landscape crosses
 *     900 too, so it keeps the same keyboard avoidance + tap-through
 *     behaviour the mobile branch has (`KeyboardAvoidingView` and
 *     `keyboardShouldPersistTaps`), otherwise the on-screen keyboard
 *     covers the field and the first tap on the CTA is swallowed by the
 *     keyboard dismissal.
 *   - Mobile (<900): wrap the body in `KeyboardAwareView` so the
 *     keyboard lifts the form without clipping. Unchanged behaviour.
 */
import React, { type ComponentRef, type RefObject } from 'react'
import { KeyboardAvoidingView, Platform, View, ScrollView } from 'react-native'
import { StatusBar } from 'expo-status-bar'
import { KeyboardAwareView } from '../ui/KeyboardAwareView'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { SPACING } from '../../lib/brand'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors } from '../../lib/theme/tokens'
import { COLUMN_PRESETS, type ColumnPreset } from '../../lib/theme/desktop-spacing'
import { LiquidCanvas } from '../ui/LiquidBackdrop'

/**
 * Same contract `KeyboardAwareView` uses on the mobile branch: iOS lifts by
 * padding, Android by height, web has no software keyboard to avoid.
 */
const KEYBOARD_BEHAVIOR =
  Platform.OS === 'ios' ? 'padding' : Platform.OS === 'android' ? 'height' : undefined

interface AuthScaffoldProps {
  /** Full form body (BackButton + header + inputs + CTA + social + footer). */
  children: React.ReactNode
  /** @deprecated ignored on desktop — layout owns the BrandPanel. Kept for back-compat. */
  showBrandPanel?: boolean
  /** @deprecated ignored on desktop — BrandPanel uses shared generic copy. */
  brandHeadline?: string
  /** @deprecated ignored on desktop — BrandPanel uses shared generic copy. */
  brandSubtitle?: string
  /**
   * Column width preset. `'form'` (480) default, `'formWide'` (520) for
   * dense register layouts. Desktop only — ignored on mobile.
   */
  columnWidth?: Extract<ColumnPreset, 'form' | 'formWide'>
  /** Optional mobile background override. Defaults to the active semantic background. */
  background?: string
  /**
   * Optional overlay (e.g. `<BiometricPrompt>`) that should render on
   * top of the layout on every breakpoint.
   */
  overlay?: React.ReactNode
  /**
   * Handle on the scroller that owns this form, so a screen can scroll to
   * its first invalid field. Works on both branches.
   */
  scrollRef?: RefObject<ComponentRef<typeof ScrollView> | null>
}

export function AuthScaffold({
  children,
  columnWidth = 'form',
  background,
  overlay,
  scrollRef,
}: AuthScaffoldProps) {
  const { width } = useBreakpoint()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const isSplit = width >= 900
  // Call sites currently pass the semantic base token, which is exactly what
  // LiquidCanvas owns. Preserve the escape hatch for a genuine one-off status
  // canvas without accidentally flattening every auth route back to a solid
  // rectangle.
  const customBackground = background && background !== colors.background ? background : undefined

  if (isSplit) {
    // Layout owns BrandPanel. This route owns one lightweight material canvas
    // behind its form; surfaces inside the form inherit the shared tokens.
    return (
      <LiquidCanvas
        ambient={!customBackground}
        style={customBackground ? { backgroundColor: customBackground } : undefined}
      >
        <KeyboardAvoidingView behavior={KEYBOARD_BEHAVIOR} style={{ flex: 1 }}>
          <ScrollView
            ref={scrollRef}
            style={{ flex: 1 }}
            contentContainerStyle={{
              flexGrow: 1,
              justifyContent: 'center',
              maxWidth: COLUMN_PRESETS[columnWidth],
              width: '100%',
              alignSelf: 'center',
              paddingVertical: SPACING['2xl'],
              paddingHorizontal: SPACING['2xl'],
            }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {children}
          </ScrollView>
        </KeyboardAvoidingView>
        {overlay}
      </LiquidCanvas>
    )
  }

  // Mobile — KeyboardAwareView handles the keyboard + scroll. The canvas is
  // static gradients rather than a live blur, so typing stays responsive on
  // Android while iOS can opt into its native material at individual surfaces.
  return (
    <LiquidCanvas
      ambient={!customBackground}
      style={customBackground ? { backgroundColor: customBackground } : undefined}
    >
      <View style={{ flex: 1 }}>
        <StatusBar style={isDark ? 'light' : 'dark'} />
        <KeyboardAwareView scrollRef={scrollRef}>{children}</KeyboardAwareView>
        {overlay}
      </View>
    </LiquidCanvas>
  )
}
